// The dashboard's derived signal layer: one pure pass over fetched data, so Activity and Needs Attention can never disagree
import { parseUpdateBlocks } from "./chatUpdates";
import { formatSpanShort } from "./dateUtils";
import { computeNextRunFromCron, parseRuntime, type FrequencyType } from "./scheduleFrequency";
import { orderedSources, savedObservationFlag } from "./composedForecastRoles";
import { isForecastFilesOutput } from "./seriesSources";
import { normalizeWeight } from "./composedForecastWeight";
import type { ProcessedModelRunHistory } from "../services/processedModelRunHistoryService";
import type {
  AgentSchedule,
  AgentScheduleRun,
  ScheduleConversation,
} from "../services/scheduleService";
import type { DataOperationConfig } from "../dataScienceOperations/types";

export type SignalSeverity = "positive" | "negative" | "attention" | "neutral";

export type SignalSubjectType = "asset" | "model" | "agent" | "forecast";

// Kind drives grouping: four identical "Wind Power run failed" rows is a wall, so repeats collapse into one carrying the count
export type SignalKind =
  | "run-failed"
  | "run-complete"
  | "agent-run-failed"
  | "agent-run-complete"
  | "agent-error"
  | "agent-overdue"
  | "model-overdue"
  | "forecast-stale";

export interface Signal {
  id: string;
  kind: SignalKind;
  at: string;
  severity: SignalSeverity;
  title: string;
  subject: { type: SignalSubjectType; id: string; label: string };
  actionable: boolean;
  // A standing fault is true now, not at `at`, so it never ages out — a feed dark three days would drop quietly out of view
  standing?: boolean;
  // Set by groupSignals when repeats of the same kind on the same subject collapse into one row
  count?: number;
}

// A ProcessedModelSchedule row, as listProcessedModelSchedules returns it.
export interface ProcessedModelScheduleLite {
  id: string;
  processedModelSchemaId: string;
  status: "ACTIVE" | "INACTIVE";
  nextRunAt: string;
  lastRunAt?: string | null;
  runCount?: number | null;
}

// A ProcessedModelOutputParam carrying only its newest ForecastRecord — enough to answer "is this feed stale".
export interface OutputParamLite {
  id: string;
  name: string;
  schemaId: string;
  active?: boolean | null;
  schema?: { id: string; name: string } | null;
  forecasts?: { items: Array<{ id: string; forecastIssued: string }> } | null;
}

// A conversation reached through the runner link, carrying its schedule so an insight can still name its agent
export interface RunnerConversation {
  scheduleId: string;
  conversation: ScheduleConversation;
}

// Just enough of a model to name it, so an overdue model with no runs in range still has a label
export interface ModelSchemaName {
  id: string;
  name?: string | null;
}

export interface OperationsData {
  runs: ProcessedModelRunHistory[];
  agentSchedules: AgentSchedule[];
  agentRuns: AgentScheduleRun[];
  modelSchedules: ProcessedModelScheduleLite[];
  outputParams: OutputParamLite[];
  assetGroups: AssetGroupLite[];
  runnerConversations: RunnerConversation[];
  modelSchemas?: ModelSchemaName[];
  /** Sources whose fetch failed — while any are listed, a zero on the board may be a missing answer rather than a real one. */
  failedSources: string[];
}

// Links land on vertical-nav destinations, not their Admin twins, so no row drops someone into an unfamiliar surface
export const MODEL_TEMPLATES_PATH = "/home/model-templates";
export const AI_ANALYST_PATH = "/home/ai-analyst";

// The time filter is global page state: per-widget ranges let two widgets quote contradictory numbers off the same data
export interface TimeWindow {
  id: "1h" | "24h" | "7d" | "30d";
  label: string;
  hours: number;
  /** Sparkline buckets across the timeWindow, so the trend is of the timeWindow rather than a fixed week. */
  buckets: number;
  bucketMs: number;
  /** Names the delta's baseline — a delta without one means nothing. */
  baseline: string;
  /** Reads after a count: "7 runs in the last 24 hours". */
  countSuffix: string;
}

const HOUR_MS = 3_600_000;

export const TIME_WINDOWS: TimeWindow[] = [
  {
    id: "1h",
    label: "Last hour",
    hours: 1,
    buckets: 6,
    bucketMs: 10 * 60_000,
    baseline: "vs previous 10 min",
    countSuffix: "in the last hour",
  },
  {
    id: "24h",
    label: "Today",
    hours: 24,
    buckets: 12,
    bucketMs: 2 * HOUR_MS,
    baseline: "vs previous 2 hrs",
    countSuffix: "in the last 24 hours",
  },
  {
    id: "7d",
    label: "Last 7 days",
    hours: 168,
    buckets: 7,
    bucketMs: 24 * HOUR_MS,
    baseline: "vs yesterday",
    countSuffix: "in the last 7 days",
  },
  {
    id: "30d",
    label: "Last 30 days",
    hours: 720,
    buckets: 15,
    bucketMs: 48 * HOUR_MS,
    baseline: "vs previous 2 days",
    countSuffix: "in the last 30 days",
  },
];

// Seven days matches what the feed already showed, so turning the control on changes nothing by itself
export const DEFAULT_TIME_WINDOW: TimeWindow =
  TIME_WINDOWS.find((timeWindow) => timeWindow.id === "7d") || TIME_WINDOWS[0];

// The shorter ranges share one 7-day fetch, so switching between them is instant rather than a reload
const RUN_FETCH_FLOOR_HOURS = 168;

export const runFetchHours = (timeWindow: TimeWindow): number =>
  Math.max(timeWindow.hours, RUN_FETCH_FLOOR_HOURS);

const hoursAgo = (hours: number, now: number) => now - hours * 3_600_000;

const time = (iso?: string | null) => (iso ? new Date(iso).getTime() : NaN);

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// A run's `description` holds failure text (900-char TLS errors on dev), so it stands in for a name only when it reads like one
const NAME_FALLBACK_MAX = 60;

const schemaLabel = (run: ProcessedModelRunHistory) => {
  const name = run.processedModelSchema?.name?.trim();
  if (name) return name;
  const description = run.description?.trim();
  if (description && description.length <= NAME_FALLBACK_MAX && !description.includes("\n")) {
    return description;
  }
  return "Model";
};

const modelRunSignals = (runs: ProcessedModelRunHistory[]): Signal[] => {
  const signals: Signal[] = [];

  runs.forEach((run) => {
    const label = schemaLabel(run);
    const at = run.completedAt || run.startedAt || run.createdAt;
    const subject = { type: "model" as const, id: run.processedModelSchemaId, label };

    if (run.status === "FAILED") {
      signals.push({
        id: `run-failed-${run.id}`,
        kind: "run-failed",
        at,
        severity: "negative",
        title: `${label} run failed`,
        subject,
        // An acknowledged failure has already been triaged by an admin, so it stops asking for a decision
        actionable: !run.acknowledged,
      });
      return;
    }

    if (run.status === "COMPLETE") {
      signals.push({
        id: `run-complete-${run.id}`,
        kind: "run-complete",
        at,
        severity: "positive",
        title: `${label} run completed`,
        subject,
        actionable: false,
      });
    }
  });

  return signals;
};

const agentSignals = (schedules: AgentSchedule[], runs: AgentScheduleRun[]): Signal[] => {
  const titleById = new Map(schedules.map((schedule) => [schedule.id, schedule.title]));
  const signals: Signal[] = [];

  runs.forEach((run) => {
    const label = titleById.get(run.scheduleId) || "Agent";
    const subject = { type: "agent" as const, id: run.scheduleId, label };
    const failed = /fail|error/i.test(run.status || "") || Boolean(run.error);
    signals.push({
      id: `agent-run-${run.id}`,
      kind: failed ? "agent-run-failed" : "agent-run-complete",
      at: run.createdAt,
      severity: failed ? "negative" : "positive",
      title: failed ? `${label} agent run failed` : `${label} agent run completed`,
      subject,
      actionable: failed,
    });
  });

  // A schedule carrying an error is a standing fault, not a past event — it keeps asking until cleared
  schedules.forEach((schedule) => {
    if (!schedule.error) return;
    signals.push({
      id: `agent-error-${schedule.id}`,
      kind: "agent-error",
      at: schedule.lastRunAt || schedule.updatedAt,
      severity: "negative",
      title: `${schedule.title} agent is erroring`,
      subject: { type: "agent", id: schedule.id, label: schedule.title },
      actionable: true,
      standing: true,
    });
  });

  return signals;
};

// Nothing in the app surfaces an overdue schedule today, and it is the cheapest real ops miss to catch.
const overdueSignals = (
  agentSchedules: AgentSchedule[],
  modelSchedules: ProcessedModelScheduleLite[],
  schemaNameById: Map<string, string>,
  now: number
): Signal[] => {
  const signals: Signal[] = [];

  agentSchedules.forEach((schedule) => {
    if (schedule.status !== "ACTIVE") return;
    const due = time(schedule.nextRunAt);
    if (!due || Number.isNaN(due) || due >= now) return;
    signals.push({
      id: `agent-overdue-${schedule.id}`,
      kind: "agent-overdue",
      at: schedule.nextRunAt,
      severity: "attention",
      title: `${schedule.title} agent is overdue`,
      subject: { type: "agent", id: schedule.id, label: schedule.title },
      actionable: true,
      standing: true,
    });
  });

  modelSchedules.forEach((schedule) => {
    if (schedule.status !== "ACTIVE") return;
    const due = time(schedule.nextRunAt);
    if (!due || Number.isNaN(due) || due >= now) return;
    const label = schemaNameById.get(schedule.processedModelSchemaId) || "Scheduled model";
    signals.push({
      id: `model-overdue-${schedule.id}`,
      kind: "model-overdue",
      at: schedule.nextRunAt,
      severity: "attention",
      title: `${label} is overdue to run`,
      subject: { type: "model", id: schedule.processedModelSchemaId, label },
      actionable: true,
      standing: true,
    });
  });

  return signals;
};

// Fixed threshold, not per-parameter: ProcessedModelOutputParam carries no cadence, and a day of silence is past explaining
const FORECAST_STALE_HOURS = 24;

const forecastStaleSignals = (params: OutputParamLite[], now: number): Signal[] =>
  params.reduce<Signal[]>((signals, param) => {
    if (param.active === false) return signals;
    const latest = param.forecasts?.items?.[0]?.forecastIssued;
    // Never issued means unconfigured or brand new, not stale — flagging it would board every half-built model on day one
    if (!latest) return signals;

    const issued = time(latest);
    if (Number.isNaN(issued) || now - issued < FORECAST_STALE_HOURS * 3_600_000) return signals;

    const label = param.schema?.name ? `${param.schema.name} · ${param.name}` : param.name;
    signals.push({
      id: `forecast-stale-${param.id}`,
      kind: "forecast-stale",
      at: latest,
      severity: "attention",
      title: `${label} last issued ${relativeShort(issued, now)}`,
      subject: { type: "forecast", id: param.id, label },
      actionable: true,
      standing: true,
    });
    return signals;
  }, []);

/** The stalest live feed, for the freshness line under the generation KPI. */
const stalestFeed = (
  params: OutputParamLite[],
  now: number = Date.now()
): { label: string; issued: number; age: string } | null => {
  let worst: { label: string; issued: number; age: string } | null = null;
  params.forEach((param) => {
    if (param.active === false) return;
    const latest = param.forecasts?.items?.[0]?.forecastIssued;
    if (!latest) return;
    const issued = time(latest);
    if (Number.isNaN(issued) || (worst && issued >= worst.issued)) return;
    worst = {
      label: param.schema?.name ? `${param.schema.name} · ${param.name}` : param.name,
      issued,
      age: relativeShort(issued, now),
    };
  });
  return worst;
};

const byTimeDesc = (a: Signal, b: Signal) => time(b.at) - time(a.at);

const SEVERITY_RANK: Record<SignalSeverity, number> = {
  negative: 0,
  attention: 1,
  neutral: 2,
  positive: 3,
};

// Alerts read worst first, so a standing error outranks a newer overdue model; ties stay newest first
const bySeverityThenTime = (a: Signal, b: Signal) =>
  SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || byTimeDesc(a, b);

/** The single derivation. Everything the dashboard's feed widgets show comes out of here. */
export const deriveSignals = (data: OperationsData, now: number = Date.now()): Signal[] => {
  const {
    runs = [],
    agentSchedules = [],
    agentRuns = [],
    modelSchedules = [],
    outputParams = [],
    modelSchemas = [],
  } = data || {};

  // Schema list first, since an overdue model may have no runs in range to take a name from
  const schemaNameById = new Map<string, string>();
  modelSchemas.forEach((schema) => {
    if (schema.name) schemaNameById.set(schema.id, schema.name);
  });
  runs.forEach((run) => {
    if (run.processedModelSchema?.name && !schemaNameById.has(run.processedModelSchemaId)) {
      schemaNameById.set(run.processedModelSchemaId, run.processedModelSchema.name);
    }
  });

  return [
    ...modelRunSignals(runs),
    ...agentSignals(agentSchedules, agentRuns),
    ...overdueSignals(agentSchedules, modelSchedules, schemaNameById, now),
    ...forecastStaleSignals(outputParams, now),
  ]
    .filter((signal) => !Number.isNaN(time(signal.at)))
    .sort(byTimeDesc);
};

/** Needs Attention: the actionable slice, newest first, inside the self-clearing timeWindow. */
export const selectNeedsAttention = (
  signals: Signal[],
  now: number = Date.now(),
  timeWindow: TimeWindow = DEFAULT_TIME_WINDOW
): Signal[] => {
  const cutoff = hoursAgo(timeWindow.hours, now);
  // Standing faults ignore the timeWindow, since filtering on `at` would hide exactly the worst cases — the longest-quiet feeds
  return signals.filter(
    (signal) => signal.actionable && (signal.standing || time(signal.at) >= cutoff)
  );
};

export interface AgentRowSummary {
  id: string;
  title: string;
  /** How often it runs — "Daily", "Hourly", "Weekly"… straight off the stored runtime. */
  cadence: FrequencyType;
  status: AgentSchedule["status"];
  /** Carried so the widget's own pause/resume can recompute the next occurrence, as the modal does. */
  cronExpression: string;
  timezone: string;
  nextRunAt: string;
  lastRunAt?: string | null;
  runCount: number;
  overdue: boolean;
  erroring: boolean;
  /** Stored owner, in whichever of the three formats wrote it — see canManageAgent. */
  owner?: string | null;
}

/** Whether this user may pause or run an agent: only the owner can write, and AdminReadOnly never can. */
export const canManageAgent = (
  agent: AgentRowSummary,
  ownerCandidates: string[],
  groups: string[] = []
): boolean => {
  if (groups.includes("AdminReadOnly") && !groups.includes("Admin")) return false;
  return Boolean(agent.owner && ownerCandidates.includes(agent.owner));
};

/** Folds one saved schedule into the list; the mutation returns no `runners`, so the spread keeps the fetched ones. */
export const mergeAgentSchedule = (
  schedules: AgentSchedule[],
  updated: AgentSchedule
): AgentSchedule[] =>
  schedules.map((schedule) => (schedule.id === updated.id ? { ...schedule, ...updated } : schedule));

/** Late only while ACTIVE — the poller skips paused schedules, so a past nextRunAt there is not a miss. */
export const isAgentOverdue = (
  agent: Pick<AgentRowSummary, "status" | "nextRunAt">,
  now: number = Date.now()
): boolean => {
  const due = time(agent.nextRunAt);
  return agent.status === "ACTIVE" && !Number.isNaN(due) && due < now;
};

/** Agents widget rows — faults and overdue first, then the ones simply waiting on their next run. */
export const summarizeAgents = (
  schedules: AgentSchedule[],
  now: number = Date.now()
): AgentRowSummary[] =>
  schedules
    .filter((schedule) => schedule.status !== "DELETED")
    .map((schedule) => {
      return {
        id: schedule.id,
        title: schedule.title,
        cadence: parseRuntime(schedule.runtime || "").type,
        status: schedule.status,
        cronExpression: schedule.cronExpression,
        timezone: schedule.timezone,
        nextRunAt: schedule.nextRunAt,
        lastRunAt: schedule.lastRunAt,
        runCount: schedule.runCount ?? 0,
        overdue: isAgentOverdue(schedule, now),
        erroring: Boolean(schedule.error),
        owner: schedule.owner,
      };
    })
    .sort((a, b) => {
      const weight = (row: AgentRowSummary) => (row.erroring ? 0 : row.overdue ? 1 : 2);
      return weight(a) - weight(b) || time(a.nextRunAt) - time(b.nextRunAt);
    });

/** Pause, or resume at the next real occurrence — the Manage Agents rules, so the two controls cannot drift apart. */
export const agentToggleInput = (agent: AgentRowSummary) =>
  agent.status === "ACTIVE"
    ? { id: agent.id, status: "PAUSED" as const }
    : {
        id: agent.id,
        status: "ACTIVE" as const,
        nextRunAt: computeNextRunFromCron(agent.cronExpression, agent.timezone),
        // The auto-pause explanation would otherwise linger after resuming
        error: null,
      };

/** Run once: due now on the poller's next cycle, with no `status` so a paused agent is never resumed. */
export const agentRunNowInput = (agent: AgentRowSummary) => ({
  id: agent.id,
  nextRunAt: new Date().toISOString(),
});

/** Whether "Run once" means anything for this agent — the poller only picks up ACTIVE schedules. */
export const canRunAgent = (agent: AgentRowSummary): boolean => agent.status === "ACTIVE";

/** The single worst thing on the board, for a headline or a KPI tile. */
const worstSignal = (signals: Signal[]): Signal | null =>
  signals.find((signal) => signal.severity === "negative") ||
  signals.find((signal) => signal.severity === "attention") ||
  null;

// --- Grouping, banding and rollups: the presentation layer's raw material ---

const GROUP_TITLE: Record<SignalKind, (label: string) => string> = {
  "run-failed": (label) => `${label} failed`,
  "run-complete": (label) => `${label} completed`,
  "agent-run-failed": (label) => `${label} agent failed`,
  "agent-run-complete": (label) => `${label} agent completed`,
  "agent-error": (label) => `${label} agent is erroring`,
  "agent-overdue": (label) => `${label} agent is overdue`,
  "model-overdue": (label) => `${label} is overdue to run`,
  "forecast-stale": (label) => `${label} has stopped issuing`,
};

/** Collapse repeats of the same kind on the same subject into one row carrying a count. */
export const groupSignals = (signals: Signal[]): Signal[] => {
  const groups = new Map<string, Signal[]>();
  signals.forEach((signal) => {
    // Keyed on id: labels fall back to a shared "Model", which would merge unrelated subjects
    const key = `${signal.kind}:${signal.subject.type}:${signal.subject.id || signal.subject.label}`;
    groups.set(key, [...(groups.get(key) || []), signal]);
  });

  return Array.from(groups.values())
    .map((members: Signal[]) => {
      const [newest] = members;
      if (members.length === 1) return newest;
      return {
        ...newest,
        // The group keeps the newest row's time, so the feed still reads chronologically
        title: GROUP_TITLE[newest.kind](newest.subject.label),
        count: members.length,
        actionable: members.some((member: Signal) => member.actionable),
      };
    })
    .sort(bySeverityThenTime);
};

// Masthead layout: the visible tile ids in order, kept in localStorage as a per-person preference costing no schema change

const KPI_LAYOUT_KEY = "erode.operations.kpiLayout";

/** The generation headline's id. No longer a row tile — the chart widget renders this figure itself. */
export const FORECAST_TILE_ID = "forecast";

// The three shipped tiles; ids are stable, and "forecast" being absent is what drops it from a saved layout
const BASE_KPI_IDS = ["attention", "runs", "agents"] as const;

/** Added tiles are one composed forecast each, namespaced so they can never collide with a base id. */
export const composedForecastTileId = (composedForecastId: string) => `cf:${composedForecastId}`;

export const isComposedForecastTile = (id: string) => id.startsWith("cf:");

export const composedForecastIdFromTile = (id: string) => id.slice(3);

/** Reads the saved order, dropping anything unrecognised so a stale entry cannot blank the row. */
export const readKpiLayout = (): string[] => {
  try {
    const raw = window.localStorage.getItem(KPI_LAYOUT_KEY);
    if (!raw) return [...BASE_KPI_IDS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...BASE_KPI_IDS];
    const ids = parsed.filter(
      (id: unknown): id is string =>
        typeof id === "string" &&
        ((BASE_KPI_IDS as readonly string[]).includes(id) || isComposedForecastTile(id))
    );
    return Array.from(new Set(ids));
  } catch {
    // A private window or a corrupt value must not take the masthead down with it
    return [...BASE_KPI_IDS];
  }
};

export const writeKpiLayout = (ids: string[]): void => {
  try {
    window.localStorage.setItem(KPI_LAYOUT_KEY, JSON.stringify(ids));
  } catch {
    // Storage denied — the row still works for this session, it just will not be remembered
  }
};

export interface Kpi {
  /** Stable identity for the saved layout — never derive it from the label, which can change. */
  id: string;
  label: string;
  value: string;
  detail: string;
  severity: SignalSeverity;
  /** Category pill above the name, as the design has it — what kind of number this is. */
  tag: string;
  /** Split off the value so the unit can sit at reading size beside a display-size figure. */
  unit?: string;
  /** Relative age, at the right edge of the meta line. Without one, a stale figure reads as live. */
  age?: string;
  // Buckets across the selected timeWindow, oldest first — only on metrics that genuinely have a series
  series?: number[];
  /** Compact token on the value row ("↑ 3"). */
  delta?: string;
  deltaDirection?: "up" | "down";
  /** What the delta is measured against — a tooltip, not a line of copy repeated on every tile. */
  deltaLabel?: string;
}

const DAY_MS = 86_400_000;

// Daily counts oldest first, bucketed over the selected timeWindow — a fixed week under "Last hour" is a contradictory range
const countByBucket = (times: number[], now: number, timeWindow: TimeWindow): number[] => {
  const buckets = new Array(timeWindow.buckets).fill(0);
  times.forEach((at) => {
    const index = timeWindow.buckets - 1 - Math.floor((now - at) / timeWindow.bucketMs);
    if (index >= 0 && index < timeWindow.buckets) buckets[index] += 1;
  });
  return buckets;
};

// Last bucket against the one before it — the comparison the timeWindow's own `baseline` names.
const bucketDelta = (series: number[]) =>
  series.length >= 2
    ? signedDelta(series[series.length - 1], series[series.length - 2])
    : undefined;

const signedDelta = (
  current: number,
  previous: number
): { delta: string; deltaDirection: "up" | "down" } | undefined => {
  // Nothing on either side of the comparison means there is no trend to report, not a flat one
  if (current === 0 && previous === 0) return undefined;
  const diff = current - previous;
  if (diff === 0) return undefined;
  return {
    delta: `${diff > 0 ? "↑" : "↓"} ${Math.abs(diff)}`,
    deltaDirection: diff > 0 ? "up" : "down",
  };
};

/** The masthead: state in a few seconds, from data the page has already fetched. */
export const operationsKpis = (
  data: OperationsData,
  signals: Signal[],
  // The grouped Alerts list the caller already built, so it is not derived twice per render
  attention: Signal[],
  now: number = Date.now(),
  timeWindow: TimeWindow = DEFAULT_TIME_WINDOW
): Kpi[] => {
  const cutoff = hoursAgo(timeWindow.hours, now);
  const runs = data.runs.filter((run) => time(run.createdAt) >= cutoff);
  const failed = runs.filter((run) => run.status === "FAILED").length;
  const worst = worstSignal(attention);
  const activeAgents = data.agentSchedules.filter((schedule) => schedule.status === "ACTIVE");
  const lateAgents = activeAgents.filter((schedule) => time(schedule.nextRunAt) < now).length;

  const runSeries = countByBucket(data.runs.map((run) => time(run.createdAt)), now, timeWindow);
  const agentRunSeries = countByBucket(
    data.agentRuns.map((run) => time(run.createdAt)),
    now,
    timeWindow
  );
  const attentionSeries = countByBucket(
    signals.filter((signal) => signal.actionable).map((signal) => time(signal.at)),
    now,
    timeWindow
  );

  // An all-clear is only earned when every source answered: with one missing, a zero reads neutral, not green
  const clear: SignalSeverity = data.failedSources.length > 0 ? "neutral" : "positive";

  return [
    // No generation tile here — that number sits inside the chart it is measured on, via generationHeadline
    {
      id: "attention",
      tag: "Attention",
      label: "Needs attention",
      value: String(attention.length),
      detail: worst ? worst.title : clear === "positive" ? "Nothing outstanding" : "Some data didn’t load",
      severity: attention.length ? "negative" : clear,
      series: attentionSeries,
      deltaLabel: timeWindow.baseline,
      ...bucketDelta(attentionSeries),
    },
    {
      id: "runs",
      tag: "Runs",
      label: "Runs",
      value: String(runs.length),
      // The baseline moved onto the delta chip's tooltip — it was the same three words on all three tiles
      detail: failed
        ? `${failed} failed · ${Math.round((failed / runs.length) * 100)}%`
        : runs.length
        ? "All completed"
        : `None ${timeWindow.countSuffix}`,
      severity: failed ? "attention" : clear,
      series: runSeries,
      deltaLabel: timeWindow.baseline,
      ...bucketDelta(runSeries),
    },
    {
      id: "agents",
      tag: "Agents",
      label: "Active agents",
      value: String(activeAgents.length),
      detail: lateAgents
        ? `${lateAgents} overdue`
        : `${data.agentSchedules.length - activeAgents.length} paused`,
      severity: lateAgents ? "attention" : clear,
      series: agentRunSeries,
      deltaLabel: timeWindow.baseline,
      ...bucketDelta(agentRunSeries),
    },
  ];
};

/** The board's failure line, or null when every source answered — a count built on a failed fetch reads exactly like a real zero. */
export const loadFailureNotice = (failedSources: string[]): string | null =>
  failedSources.length > 0
    ? `Couldn’t load ${failedSources.join(", ")} — counts on this board may be short.`
    : null;

// Compact age for a KPI value or a row, where "about 4 hours ago" is too long for the width it gets.
export function relativeShort(at: number, now: number): string {
  const mins = Math.max(0, Math.round((now - at) / 60_000));
  return mins < 1 ? "Just now" : `${formatSpanShort(mins)} ago`;
}

// The forward twin of relativeShort, for a next run; never "in 0m", since overdue is its own state
export function untilShort(at: string, now: number): string {
  const mins = Math.round((new Date(at).getTime() - now) / 60_000);
  if (Number.isNaN(mins)) return "Scheduled";
  return `in ${formatSpanShort(Math.max(1, mins))}`;
}

// Generation against its own forecast — read off the ComposedForecast that already wires the two together

/** The actual line: a feed and an uploaded dataset are both real readings, differing only in where the series is fetched. */
export type GenerationActual =
  | { kind: "postgres"; parameterId: number; lookbackDays: number }
  | {
      kind: "dataset";
      datasetId: string;
      datasetRecordS3Path?: string;
      datasetRecordFileName?: string;
      datasetName?: string;
      /** The dataset source's saved operation, so the tile reads the same series the form charts. */
      operation?: DataOperationConfig;
      /** A curated dataset is read by column over a range (stage 5, decision 4). */
      curated?: boolean;
      column?: string;
      lookbackDays?: number;
    };

/** Every source the app can fetch a series for. The form creates exactly these three, so the set is closed. */
export type GenerationSeriesSource = { kind: "model"; outputParamId: string } | GenerationActual;

export interface GenerationSeriesRef {
  /** The data source's own id — what keeps a line stable across refetches. */
  id: string;
  label: string;
  source: GenerationSeriesSource;
  /** The source's multiplier (a unit bridge), applied after the fetch the same way the Custom View applies it. */
  weight?: number;
}

export interface GenerationPair {
  composedForecastId: string;
  composedForecastName: string;
  // PROCESSED_MODEL_OUTPUT source — the forecast line
  outputParamId: string;
  forecastSourceName: string;
  /** The forecast source's multiplier. */
  forecastWeight?: number;
  /** Every non-forecast source, in priority order; the actual is the one the view marks as its observation, else whichever overlaps the model once fetched, since order cannot tell °C from MW. */
  candidates: GenerationSeriesRef[];
  /** The candidate the Custom View flags as its observation (metadata.accuracy), when one is (stage 5, decision 8). */
  observationSourceId?: string;
}

const DEFAULT_LOOKBACK_DAYS = 7;

const readMetadata = (raw: unknown): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === "object") return raw as Record<string, any>;
  if (typeof raw !== "string") return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

// The slice of a ComposedForecast data source the pairing reads; metadata and dataOperations are AWSJSON, string or object
interface DataSourceLite {
  id?: string | null;
  sourceType?: string | null;
  sourceName?: string | null;
  isActive?: boolean | null;
  priority?: number | null;
  metadata?: unknown;
  dataOperations?: unknown;
  processedModelOutputId?: string | null;
  processedModelOutput?: { name?: string | null; storageMode?: string | null } | null;
  weight?: number | null;
}

// A ComposedForecast as listComposedForecastsCustom returns it, narrowed to what selectGenerationPairs reads
export interface ComposedForecastLite {
  id: string;
  name: string;
  isActive?: boolean | null;
  lastProcessed?: string | null;
  updatedAt?: string | null;
  dataSources?: { items?: Array<DataSourceLite | null> | null } | null;
}

// Mirrors parseOperationFromDataOperations (args/version defaulted), inlined since composedForecastUtils pulls in an API client
const readOperation = (raw: unknown): DataOperationConfig | undefined => {
  const first = readMetadata(raw)?.pipeline?.[0];
  if (!first?.id) return undefined;
  return { id: first.id, args: first.args ?? {}, version: first.version ?? 1 };
};

/** Reads a data source as an actual line, or null; a dataset is stored as S3_DATA, the enum having no DATASET member. */
const readActual = (source: DataSourceLite): { actual: GenerationActual; label: string } | null => {
  const meta = readMetadata(source?.metadata);

  if (source?.sourceType === "POSTGRES_HISTORICAL" && Number.isFinite(Number(meta.parameterId))) {
    return {
      actual: {
        kind: "postgres",
        parameterId: Number(meta.parameterId),
        lookbackDays:
          Number(meta.lookbackDays) > 0 ? Number(meta.lookbackDays) : DEFAULT_LOOKBACK_DAYS,
      },
      label: meta.parameterName || source.sourceName || "Actual",
    };
  }

  if (source?.sourceType === "S3_DATA" && meta.datasetId) {
    if (meta.curated && meta.column) {
      return {
        actual: {
          kind: "dataset",
          datasetId: String(meta.datasetId),
          datasetName: meta.datasetName,
          curated: true,
          column: String(meta.column),
          lookbackDays: Number(meta.lookbackDays) > 0 ? Number(meta.lookbackDays) : DEFAULT_LOOKBACK_DAYS,
        },
        label: source.sourceName || `${meta.datasetName || "Dataset"} · ${meta.column}`,
      };
    }
    return {
      actual: {
        kind: "dataset",
        datasetId: String(meta.datasetId),
        datasetRecordS3Path: meta.datasetRecordS3Path,
        datasetRecordFileName: meta.datasetRecordFileName,
        datasetName: meta.datasetName,
        operation: readOperation(source.dataOperations),
      },
      label: meta.datasetRecordFileName || meta.datasetName || source.sourceName || "Actual",
    };
  }

  return null;
};

/** Any source as something fetchable, with its weight, or null. */
const readSeriesSource = (source: DataSourceLite): GenerationSeriesRef | null => {
  const ref = readSeriesSourceShape(source);
  return ref ? { ...ref, weight: normalizeWeight(source?.weight) } : null;
};

/** Any source as something fetchable, or null — the model branch plus whatever readActual already understands. */
const readSeriesSourceShape = (source: DataSourceLite): GenerationSeriesRef | null => {
  if (source?.sourceType === "PROCESSED_MODEL_OUTPUT" && source.processedModelOutputId) {
    return {
      id: source.id || `model:${source.processedModelOutputId}`,
      label: source.processedModelOutput?.name || source.sourceName || "Model output",
      source: { kind: "model", outputParamId: source.processedModelOutputId },
    };
  }

  const actual = readActual(source);
  return actual ? { id: source.id || `src:${actual.label}`, label: actual.label, source: actual.actual } : null;
};

/** Every composed forecast that can answer "actual vs forecast", freshest first; a pair needs both sides configured. */
export const selectGenerationPairs = (composedForecasts: ComposedForecastLite[]): GenerationPair[] => {
  const candidates = (composedForecasts || [])
    .filter((forecast) => forecast?.isActive !== false)
    .map((forecast) => {
      // Lower priority first, as the schema defines it — unsorted, which of two feeds became the actual was list order
      // In the Custom View's list order first (which forecast is the main one), then by priority
      const sources = orderedSources(forecast.dataSources?.items)
        .filter((source): source is DataSourceLite => Boolean(source) && source?.isActive !== false)
        // Stable, so the sources sharing the default 0 keep the order they already had
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

      // The forecast line: a model output with dated issues (the same rule as composedForecastRoles.isForecastOutputSource)
      const model = sources.find(
        (source) =>
          source.sourceType === "PROCESSED_MODEL_OUTPUT" &&
          source.processedModelOutputId &&
          isForecastFilesOutput(source.processedModelOutput)
      );
      const outputParamId = model?.processedModelOutputId;
      // Everything that is not the forecast line — all of it fetched and charted, one of it chosen as the actual
      const candidates = sources
        .filter((source) => source !== model)
        .map(readSeriesSource)
        .filter(Boolean) as GenerationSeriesRef[];
      if (!model || !outputParamId || candidates.length === 0) return null;
      // The line the view marks as its observation is the actual when it is among the candidates (decision 8)
      // The Custom View's observation flag (metadata.accuracy), read by the same rule as the view itself
      const flagged = sources.find((source) => source !== model && savedObservationFlag(source));
      const observationSourceId = flagged && candidates.some((c) => c.id === flagged.id) ? flagged.id : undefined;

      return {
        composedForecastId: forecast.id,
        composedForecastName: forecast.name,
        outputParamId,
        forecastSourceName: model.processedModelOutput?.name || model.sourceName || "Forecast",
        forecastWeight: normalizeWeight(model.weight),
        candidates,
        observationSourceId,
        // Freshest configuration wins — a forecast nobody has processed in months is not the headline
        rank: time(forecast.lastProcessed) || time(forecast.updatedAt) || 0,
      };
    })
    .filter(Boolean) as Array<GenerationPair & { rank: number }>;

  // Freshest configuration first — a forecast nobody has processed in months is not the headline
  return candidates
    .sort((a, b) => (b.rank || 0) - (a.rank || 0))
    .map(({ rank, ...pair }) => pair);
};

export interface SeriesPoint {
  at: number;
  value: number;
}

export interface GenerationComparison {
  composedForecastName: string;
  actualLabel: string;
  forecastLabel: string;
  unit?: string;
  actual: SeriesPoint[];
  forecast: SeriesPoint[];
  /** Newest actual reading, the KPI's headline value. */
  latestActual: SeriesPoint | null;
  /** Positive = running over forecast, negative = under. Null when the two series never overlap. */
  deviationPct: number | null;
  /** How many actual readings found a forecast point to compare against. */
  matchedPoints: number;
  /** The candidates not chosen as the actual — charted, but not what the number is measured on. */
  extras: Array<{ id: string; label: string; points: SeriesPoint[] }>;
}

const toPoints = (timestamps: Date[], values: number[]): SeriesPoint[] =>
  timestamps
    .map((timestamp, index) => ({ at: timestamp.getTime(), value: values[index] }))
    .filter((point) => !Number.isNaN(point.at) && typeof point.value === "number" && Number.isFinite(point.value))
    .sort((a, b) => a.at - b.at);

// Half the forecast's own cadence, since a fixed tolerance would either drop real matches or invent ones that never happened
const matchTolerance = (forecast: SeriesPoint[]) => {
  if (forecast.length < 2) return 3_600_000;
  const gaps = forecast.slice(1).map((point, index) => point.at - forecast[index].at);
  return Math.max(median(gaps) / 2, 60_000);
};

/** Sum a candidate against the forecast by nearest timestamp — the overlap that decides whether it is the actual. */
const matchAgainst = (actual: SeriesPoint[], forecast: SeriesPoint[]) => {
  const tolerance = matchTolerance(forecast);

  let actualSum = 0;
  let forecastSum = 0;
  let matchedPoints = 0;
  let cursor = 0;

  // Both series are sorted, so the search walks forward once rather than scanning the forecast per point
  actual.forEach((point) => {
    while (cursor + 1 < forecast.length && forecast[cursor + 1].at <= point.at) cursor += 1;
    const near = [forecast[cursor], forecast[cursor + 1]].filter(Boolean);
    const nearest = near.reduce<SeriesPoint | null>(
      (best, candidate) =>
        !best || Math.abs(candidate.at - point.at) < Math.abs(best.at - point.at) ? candidate : best,
      null
    );
    if (!nearest || Math.abs(nearest.at - point.at) > tolerance) return;
    actualSum += point.value;
    forecastSum += nearest.value;
    matchedPoints += 1;
  });

  return { actualSum, forecastSum, matchedPoints };
};

export interface CandidateSeries {
  id: string;
  label: string;
  /** null where the fetch failed — dropped rather than charted as an empty line */
  series: { timestamps: Date[]; values: number[] } | null;
}

/** Picks the actual that overlaps the model in time, then measures the forecast against it. */
export const compareGenerationToForecast = (
  pair: GenerationPair,
  forecastSeries: { timestamps: Date[]; values: number[]; unit?: string },
  candidateSeries: CandidateSeries[]
): GenerationComparison => {
  const forecast = toPoints(forecastSeries.timestamps, forecastSeries.values);

  const scored = candidateSeries
    .filter((candidate) => candidate.series)
    .map((candidate) => {
      const points = toPoints(candidate.series!.timestamps, candidate.series!.values);
      return { ...candidate, points, match: matchAgainst(points, forecast) };
    })
    .filter((candidate) => candidate.points.length > 0);

  // The view's flagged observation first, when it overlaps the forecast at all; otherwise the best overlap (a
  // flagged series whose data stopped before the forecast's span scores nothing while a raw feed still matches).
  // Strict >, so a tie keeps the earlier candidate and priority order still decides where overlap cannot
  const flagged = pair.observationSourceId ? scored.find((candidate) => candidate.id === pair.observationSourceId) : undefined;
  const bestOverlap = scored.reduce<(typeof scored)[number] | null>(
    (best, candidate) => (!best || candidate.match.matchedPoints > best.match.matchedPoints ? candidate : best),
    null
  );
  const chosen = flagged && (flagged.match.matchedPoints > 0 || !bestOverlap?.match.matchedPoints) ? flagged : bestOverlap;

  const actual = chosen?.points ?? [];
  const { actualSum = 0, forecastSum = 0, matchedPoints = 0 } = chosen?.match ?? {};

  return {
    composedForecastName: pair.composedForecastName,
    actualLabel: chosen?.label ?? pair.candidates[0]?.label ?? "Actual",
    forecastLabel: pair.forecastSourceName,
    unit: forecastSeries.unit,
    actual,
    forecast,
    latestActual: actual.length ? actual[actual.length - 1] : null,
    // A zero forecast total has no percentage to give — say nothing rather than divide by it
    deviationPct:
      matchedPoints > 0 && forecastSum !== 0
        ? ((actualSum - forecastSum) / Math.abs(forecastSum)) * 100
        : null,
    matchedPoints,
    extras: scored
      .filter((candidate) => candidate.id !== chosen?.id)
      .map(({ id, label, points }) => ({ id, label, points })),
  };
};

/** The span the pair occupies; the chart clamps to it, since a years-old extra source would squeeze the compared readings to a few pixels. */
export const comparisonWindow = (
  comparison: GenerationComparison | null
): { min: number; max: number } | null => {
  if (!comparison) return null;

  let min = Infinity;
  let max = -Infinity;
  // Walked rather than spread into Math.min — these series run to thousands of points
  [comparison.actual, comparison.forecast].forEach((series) =>
    series.forEach((point) => {
      if (point.at < min) min = point.at;
      if (point.at > max) max = point.at;
    })
  );

  return Number.isFinite(min) ? { min, max } : null;
};

/** How many extras land inside the charted span — the rest are drawn, but off the end of the axis. */
export const extrasInWindow = (
  comparison: GenerationComparison | null,
  range: { min: number; max: number } | null
): number => {
  if (!comparison) return 0;
  if (!range) return comparison.extras.length;

  return comparison.extras.filter((extra) =>
    extra.points.some((point) => point.at >= range.min && point.at <= range.max)
  ).length;
};

// Display size can't carry six significant figures, so a value is shown at three and keeps its unit.
const compactValue = (value: number): string => {
  const magnitude = Math.abs(value);
  if (magnitude >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (magnitude >= 10) return value.toFixed(1);
  if (magnitude >= 1) return value.toFixed(2);
  return value.toFixed(3);
};

// Below this the two lines are the same line, and "0.3% over forecast" reads as false precision.
const TRACKING_BAND_PCT = 1;
// Above this the miss is worth a second look rather than just a number.
const DEVIATION_ATTENTION_PCT = 10;

// Daily means oldest first, null when a day is empty — a gap drawn as zero is a cliff that never happened
const meanByDay = (points: SeriesPoint[], now: number, days: number): number[] | null => {
  if (!points.length) return null;
  // Anchored on the newest reading, not now, so a backfilled feed still has a shape; for a live feed the two agree anyway
  const anchor = points[points.length - 1].at || now;
  const sums = new Array(days).fill(0);
  const counts = new Array(days).fill(0);
  points.forEach((point) => {
    const index = days - 1 - Math.floor((anchor - point.at) / DAY_MS);
    if (index < 0 || index >= days) return;
    sums[index] += point.value;
    counts[index] += 1;
  });

  // A gap loses its own day, not the whole line — one missing reading is no reason to show no trend at all
  const filled = sums
    .map((sum, index) => (counts[index] ? Number((sum / counts[index]).toFixed(3)) : null))
    .filter((value): value is number => value !== null);

  // Two points draw a line but say nothing; below three there is no trend to read
  return filled.length >= 3 ? filled : null;
};

/** The generation-vs-forecast headline: value, direction, and the run of actuals behind it. */
export const generationKpi = (
  comparison: GenerationComparison | null,
  freshest: { label: string; age: string } | null,
  now: number = Date.now(),
  // The comparison is still in flight on its own slower query, so the tile must not yet claim nothing is configured
  pending = false,
  // A pair that is configured but whose data would not load is a different tile from one that was never paired
  failed = false,
  // Added tiles carry their forecast's own name and id; the masthead falls back to the generic pair
  label = "Generation vs forecast",
  id = FORECAST_TILE_ID
): Kpi => {
  if (pending) {
    return {
      id,
      tag: "Forecast",
      label,
      value: "—",
      detail: "Comparing generation against forecast…",
      severity: "neutral",
    };
  }

  if (failed && !comparison) {
    return {
      id,
      tag: "Forecast",
      label,
      value: "—",
      detail: "Comparison could not be loaded",
      severity: "neutral",
    };
  }

  if (!comparison || !comparison.latestActual) {
    return {
      id,
      tag: "Forecast",
      label,
      value: "—",
      detail: comparison ? "No readings to compare" : "No forecast pairs actuals with a model",
      severity: "neutral",
    };
  }

  const { latestActual, deviationPct, unit } = comparison;
  const series = meanByDay(comparison.actual, now, 7);

  const detail =
    deviationPct === null
      ? `${comparison.actualLabel} · no overlap with forecast`
      : Math.abs(deviationPct) < TRACKING_BAND_PCT
      ? `Tracking forecast · ${comparison.composedForecastName}`
      : `${comparison.composedForecastName}${
          freshest ? ` · stalest feed ${freshest.age}` : ""
        }`;

  return {
    id,
    tag: "Forecast",
    label,
    value: compactValue(latestActual.value),
    unit,
    // The reading's own age, on the meta line — a headline generation number that is six hours old lies
    age: relativeShort(latestActual.at, now),
    detail,
    severity:
      deviationPct !== null && Math.abs(deviationPct) >= DEVIATION_ATTENTION_PCT
        ? "attention"
        : "neutral",
    series: series || undefined,
    // Inside the band there is no delta and so no direction — a bare "down" muted the sparkline on a forecast tracking perfectly
    ...(deviationPct === null || Math.abs(deviationPct) < TRACKING_BAND_PCT
      ? {}
      : {
          delta: `${deviationPct > 0 ? "+" : "−"}${Math.abs(deviationPct).toFixed(1)}%`,
          deltaDirection: deviationPct > 0 ? ("up" as const) : ("down" as const),
        }),
  };
};

// The masthead number for the chart widget: the added tiles' own figure, named for whatever the picker is on
export const generationHeadline = (
  data: OperationsData,
  comparison: GenerationComparison | null,
  now: number = Date.now(),
  // Arrives late by design: the board renders on the fast query and this fills in rather than holding it back
  pending = false,
  // Set when the comparison query rejected — the pair exists, its data did not arrive
  failed = false
): Kpi =>
  generationKpi(
    comparison,
    stalestFeed(data.outputParams || [], now),
    now,
    pending,
    failed,
    comparison?.composedForecastName || undefined
  );

// Reads the answers agent runs already left as nested MCPMessages, so it costs nothing — a fresh sentence would need erode-mcp

export interface AnalystInsight {
  id: string;
  text: string;
  at: string;
  agentLabel: string;
  href: string;
}

/** Non-findings (a restated task, an apology), skipped so the search falls through to the next paragraph. */
const NOT_AN_ANSWER = [
  // "The user's question is: …", "User request: …" — the prompt, echoed back
  /^(?:the\s+)?users?'?s?\s+(?:question|request|query|prompt|ask)\b/i,
  /^(?:here\s+is|this\s+is)\s+(?:the\s+)?(?:user'?s?\s+)?(?:question|request|task)\b/i,
  // Failure notices: an error is a state for Alerts to carry, not an insight in the agent's voice
  /^(?:we|i)\s+(?:were|was|are|am)\s+unable\s+to\b/i,
  /^(?:sorry|apologies|unfortunately)\b/i,
  /^(?:an\s+)?error\s+(?:occurred|has\s+occurred)\b/i,
  /unable\s+to\s+process\s+this\s+request/i,
  // A bare acknowledgement, with nothing after it
  /^(?:okay|ok|sure|understood|certainly)[.!]?$/i,
  // Prompt text persisted as an assistant turn; case-SENSITIVE, since "REMEMBER to…" instructs but "Note that generation fell 12%" reports
  /^(?:REMEMBER|IMPORTANT|NOTE|WARNING|CAUTION|ALWAYS|NEVER|DO NOT)\b/,
  // An answer reports on the world; only an instruction talks about the answer
  /\byour response\b/i,
  // Persona boilerplate — what the agent is, never what it found
  /^i(?:'m| am| provide| specialise| specialize)\b/i,
];

const isNotAnAnswer = (block: string) => NOT_AN_ANSWER.some((pattern) => pattern.test(block));

// Agent answers are markdown, so strip the syntax that would render as literal asterisks and hashes in a plain-text row
const firstSentence = (markdown: string): string => {
  // Stored content leads with streamed <update>/<thinking> progress, so take the body every other chat surface shows
  const answer = parseUpdateBlocks(markdown).response;
  const cleaned = answer
    // A run persisted mid-stream leaves an unclosed block the shared parser can't pair off
    .replace(/<(?:update|thinking)>[\s\S]*$/, "")
    .replace(/```[\s\S]*?```/g, "\n\n")
    // A heading labels what follows rather than being prose — dropped whole, or "Summary" welds onto the first sentence
    .replace(/^\s{0,3}#{1,6}\s+.*$/gm, "")
    .replace(/^\s{0,3}([-*_])\s*\1\s*\1[\s*\-_]*$/gm, "")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/^\s{0,3}>\s?/gm, "")
    .replace(/[*_`]/g, "");

  // Paragraphs survive the strip so the first *block* of prose is found, not the first stray word
  const paragraph = cleaned
    .split(/\n\s*\n/)
    .map((block) => block.replace(/\s+/g, " ").trim())
    // An answer can open with a bare "RESPONSE:" label, which rendered as the whole insight on the board
    .find((block) => block && !/^[^.!?]{0,40}:$/.test(block) && !isNotAnAnswer(block));
  if (!paragraph) return "";

  // Split on sentence enders, but not on the decimal point in a number the answer is reporting
  const match = paragraph.match(/^.*?[.!?](?=\s|$)/);
  const sentence = (match ? match[0] : paragraph).trim();
  return sentence.length > 240 ? `${sentence.slice(0, 237).trimEnd()}…` : sentence;
};

/** The newest answer per agent, newest first; runs carry their conversations, and `runnerConversations` covers runner-linked ones. */
export const latestAnalystInsights = (
  runs: AgentScheduleRun[],
  schedules: AgentSchedule[],
  runnerConversations: RunnerConversation[] = []
): AnalystInsight[] => {
  const titleById = new Map(schedules.map((schedule) => [schedule.id, schedule.title]));
  // One insight per agent, not per run — three nights of the same agent is one voice repeating itself
  const bySchedule = new Map<string, AnalystInsight>();

  const record = (scheduleId: string, conversation: ScheduleConversation) => {
    if (bySchedule.has(scheduleId)) return;
    const answer = [...(conversation.messages?.items || [])]
      .sort((a, b) => time(b.createdAt) - time(a.createdAt))
      .find((message) => !message.isUser && firstSentence(message.content || ""));
    if (!answer) return;

    bySchedule.set(scheduleId, {
      id: answer.id,
      text: firstSentence(answer.content),
      at: answer.createdAt,
      agentLabel: titleById.get(scheduleId) || "Agent",
      href: `${AI_ANALYST_PATH}/${conversation.id}`,
    });
  };

  [...runs]
    .sort((a, b) => time(b.createdAt) - time(a.createdAt))
    .forEach((run) => {
      (run.conversations?.items || []).forEach((conversation) => record(run.scheduleId, conversation));
    });

  [...runnerConversations]
    .sort((a, b) => time(b.conversation.lastMessageAt) - time(a.conversation.lastMessageAt))
    .forEach(({ scheduleId, conversation }) => record(scheduleId, conversation));

  return Array.from(bySchedule.values()).sort((a, b) => time(b.at) - time(a.at));
};

export interface AnalystSuggestion {
  id: string;
  /** The chip's text — short enough for a narrow card. */
  label: string;
  /** What is actually sent to the analyst; the label is only its handle. */
  question: string;
}

/** Starter questions built from what the board already worked out is wrong — a generic starter wastes this page's one advantage. */
// Three, not five: in a one-column card the chips each take their own line, and five crushed the invitation above them
const ANALYST_SUGGESTION_LIMIT = 3;

// A signal's own question, or null when its kind has none to ask
const signalQuestion = (signal: Signal): { label: string; question: string } | null => {
  const subject = signal.subject.label;
  const asked: Record<string, { label: string; question: string }> = {
    "run-failed": {
      label: `Why did ${subject} fail?`,
      question: `${subject} failed its last run. What caused it and what should I do about it?`,
    },
    "agent-run-failed": {
      label: `Why did ${subject} fail?`,
      question: `The ${subject} agent failed its last run. What caused it and what should I do about it?`,
    },
    "agent-error": {
      label: `What is wrong with ${subject}?`,
      question: `The ${subject} agent is erroring. What is wrong and how do I fix it?`,
    },
    "model-overdue": {
      label: `Why is ${subject} overdue?`,
      question: `${subject} has not run when it was scheduled to. What is blocking it?`,
    },
    "agent-overdue": {
      label: `Why is ${subject} overdue?`,
      question: `The ${subject} agent has not run when it was scheduled to. What is blocking it?`,
    },
    "forecast-stale": {
      label: `Why is ${subject} stale?`,
      question: `The forecast feed for ${subject} has stopped updating. What changed and how far behind is it?`,
    },
  };
  return asked[signal.kind] || null;
};

export const analystSuggestions = (
  signals: Signal[],
  assets: AssetCard[],
  timeWindow: TimeWindow
): AnalystSuggestion[] => {
  const suggestions: AnalystSuggestion[] = [];

  // Worst-first from selectNeedsAttention, and the full row rather than one, since a real fault beats any generic starter
  signals.slice(0, ANALYST_SUGGESTION_LIMIT).forEach((signal) => {
    const asking = signalQuestion(signal);
    if (asking) suggestions.push({ id: `signal-${signal.id}`, ...asking });
  });

  // Only worth offering when the map actually has something coloured — otherwise the answer is "none"
  if (assets.some((asset) => asset.severity === "negative" || asset.severity === "attention")) {
    suggestions.push({
      id: "assets-affected",
      label: "Which assets are affected?",
      question: "Which of my assets are affected by the models that are failing right now, and how badly?",
    });
  }

  suggestions.push({
    id: "timeWindow-summary",
    label: `What happened ${timeWindow.countSuffix}?`,
    question: `Summarise what happened across my models and agents ${timeWindow.countSuffix}, worst first.`,
  });

  // Generic, so they go last and only fill the row out when the board is too quiet to fill it itself
  const fillers: AnalystSuggestion[] = [
    {
      id: "watchlist",
      label: "What should I watch today?",
      question: "Across my assets, models and forecasts, what should I be keeping an eye on today?",
    },
    {
      id: "forecast-accuracy",
      label: "How are my forecasts tracking?",
      question: "How are my forecasts tracking against actual generation, and which one is furthest off?",
    },
    // Asks about the assets themselves, so it is only a question at all once there are some
    ...(assets.length > 0
      ? [
          {
            id: "busiest-assets",
            label: "Which assets ran most?",
            question: `Which of my assets had the most model activity ${timeWindow.countSuffix}, and which had none?`,
          },
        ]
      : []),
  ];
  fillers.forEach((filler) => {
    if (suggestions.length < ANALYST_SUGGESTION_LIMIT) suggestions.push(filler);
  });

  return suggestions.slice(0, ANALYST_SUGGESTION_LIMIT);
};

// The map answers "where is the thing that needs me", so an asset takes the worst signal on a schema linked to it

export interface AssetLite {
  id: string;
  name?: string | null;
  lat: number;
  lng: number;
  icon?: string | null;
  description?: string | null;
  linkedProcessedModelSchemas?: {
    items: Array<{ processedModelSchemaID: string; processedModelSchema?: { id: string; name: string } | null }>;
  } | null;
  linkedComposedForecasts?: {
    items: Array<{ composedForecastID: string; composedForecast?: { id: string; name: string } | null }>;
  } | null;
}

export interface AssetGroupLite {
  id: string;
  name: string;
  assets?: { items: AssetLite[] } | null;
}

export interface AssetCard {
  id: string;
  name: string;
  groupId: string;
  groupName: string;
  lat: number;
  lng: number;
  icon?: string | null;
  modelLabels: string[];
  /** The composed forecasts this asset links to — where its live generation reading comes from. */
  forecastIds: string[];
  forecastLabels: string[];
  /** Runs in the timeWindow — a count on the card, not a shape: a run tally is not the shape of a MW reading. */
  runCount: number;
  severity: SignalSeverity;
  /** What this asset's models are doing right now, in words. */
  statusLabel: string;
  /** The signal driving the status, so a card can link straight at the problem. */
  signal: Signal | null;
}

/** One card per asset, worst-first, off the signals the page already derived — so the map cannot disagree with Needs Attention. */
export const assetCards = (
  groups: AssetGroupLite[],
  attentionSignals: Signal[],
  // Run history is what gives a card its sparkline. Optional so the derivation still stands on its own.
  runs: ProcessedModelRunHistory[] = [],
  now: number = Date.now(),
  timeWindow: TimeWindow = DEFAULT_TIME_WINDOW
): AssetCard[] => {
  // Model subjects carry the schema id, which is exactly what the asset join links against
  const worstBySchema = new Map<string, Signal>();
  attentionSignals.forEach((signal) => {
    if (signal.subject.type !== "model") return;
    const seen = worstBySchema.get(signal.subject.id);
    if (!seen || SEVERITY_RANK[signal.severity] < SEVERITY_RANK[seen.severity]) {
      worstBySchema.set(signal.subject.id, signal);
    }
  });

  // Run times per schema, so an asset's series is a lookup rather than a scan of every run per asset
  const timesBySchema = new Map<string, number[]>();
  runs.forEach((run) => {
    const at = time(run.createdAt);
    if (Number.isNaN(at)) return;
    const bucket = timesBySchema.get(run.processedModelSchemaId) || [];
    bucket.push(at);
    timesBySchema.set(run.processedModelSchemaId, bucket);
  });

  const cards: AssetCard[] = [];

  (groups || []).forEach((group) => {
    (group.assets?.items || []).forEach((asset) => {
      // An asset with no coordinates cannot be placed, and a pin at 0,0 in the Atlantic is worse than none
      if (typeof asset.lat !== "number" || typeof asset.lng !== "number") return;

      const links = asset.linkedProcessedModelSchemas?.items || [];
      const modelLabels = links
        .map((link) => link.processedModelSchema?.name)
        .filter((name): name is string => Boolean(name));
      const forecastLinks = asset.linkedComposedForecasts?.items || [];
      const forecastIds = forecastLinks.map((link) => link.composedForecastID);
      const forecastLabels = forecastLinks
        .map((link) => link.composedForecast?.name)
        .filter((name): name is string => Boolean(name));

      const schemaIds = links.map((link) => link.processedModelSchemaID);
      // Only the runs inside the timeWindow: the sparkline has to be of the range the page is answering for
      const cutoff = hoursAgo(timeWindow.hours, now);
      const runTimes = schemaIds
        .flatMap((schemaId) => timesBySchema.get(schemaId) || [])
        .filter((at) => at >= cutoff);

      const hits = links
        .map((link) => worstBySchema.get(link.processedModelSchemaID))
        .filter((signal): signal is Signal => Boolean(signal))
        .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
      const signal = hits[0] || null;

      cards.push({
        id: asset.id,
        name: asset.name || "Unnamed asset",
        groupId: group.id,
        groupName: group.name,
        lat: asset.lat,
        lng: asset.lng,
        icon: asset.icon,
        modelLabels,
        forecastIds,
        forecastLabels,
        runCount: runTimes.length,
        severity: signal ? signal.severity : "neutral",
        statusLabel: signal
          ? signal.title
          : links.length === 0
          ? "No models linked"
          : `${links.length} ${links.length === 1 ? "model" : "models"} · clean`,
        signal,
      });
    });
  });

  return cards.sort(
    (a, b) =>
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.name.localeCompare(b.name)
  );
};

// A card's live number: what the asset is actually generating, against the forecast it was measured on

/** What an asset is doing right now, read off the composed forecast linked to it. */
export interface AssetReading {
  forecastName: string;
  value: string;
  unit?: string;
  /** The reading's own age — a generation number without one invites reading a stale figure as live. */
  age: string;
  /** Signed deviation against forecast, or null inside the tracking band where the two lines are one line. */
  delta: string | null;
  deltaDirection: "up" | "down";
}

/** The first linked forecast with an actual reading behind it — an asset can link several, but a card shows one. */
export const assetReading = (
  card: AssetCard,
  comparisons: Map<string, GenerationComparison | null>,
  now: number = Date.now()
): AssetReading | null => {
  for (const forecastId of card.forecastIds) {
    const comparison = comparisons.get(forecastId);
    if (!comparison?.latestActual) continue;

    const { deviationPct } = comparison;
    // Inside the band the two lines are the same line, and "0.3% over" on a 176px card reads as false precision
    const tracking = deviationPct === null || Math.abs(deviationPct) < TRACKING_BAND_PCT;

    return {
      forecastName: comparison.composedForecastName,
      value: compactValue(comparison.latestActual.value),
      unit: comparison.unit,
      age: relativeShort(comparison.latestActual.at, now),
      delta: tracking
        ? null
        : `${deviationPct! > 0 ? "+" : "−"}${Math.abs(deviationPct!).toFixed(1)}%`,
      deltaDirection: deviationPct !== null && deviationPct > 0 ? "up" : "down",
    };
  }

  return null;
};

// Each reading is an S3 download, so the map cannot be allowed to open one per asset on an org with fifty
const ASSET_READING_CAP = 8;

/** The pairs worth fetching for the map: linked to an asset, pairable, deduped and capped. */
export const assetForecastPairs = (
  cards: AssetCard[],
  pairs: GenerationPair[],
  cap: number = ASSET_READING_CAP
): GenerationPair[] => {
  const byId = new Map(pairs.map((pair) => [pair.composedForecastId, pair]));
  const wanted = new Map<string, GenerationPair>();

  // Cards arrive worst-first, so the cap spends its budget on the assets that already need looking at
  cards.forEach((card) => {
    card.forecastIds.forEach((forecastId) => {
      const pair = byId.get(forecastId);
      if (pair && !wanted.has(forecastId) && wanted.size < cap) wanted.set(forecastId, pair);
    });
  });

  return Array.from(wanted.values());
};

/** Group options for the map widget's filter, with counts, newest concern first. */
export const assetGroupOptions = (
  cards: AssetCard[]
): Array<{ id: string; name: string; count: number; affected: number }> => {
  const groups = new Map<string, { id: string; name: string; count: number; affected: number }>();
  cards.forEach((card) => {
    const entry = groups.get(card.groupId) || {
      id: card.groupId,
      name: card.groupName,
      count: 0,
      affected: 0,
    };
    entry.count += 1;
    if (card.signal) entry.affected += 1;
    groups.set(card.groupId, entry);
  });
  return Array.from(groups.values()).sort(
    (a, b) => b.affected - a.affected || a.name.localeCompare(b.name)
  );
};

/** Apply the map widget's two filters. Kept out of the component so the map and the list can't diverge. */
export const filterAssetCards = (
  cards: AssetCard[],
  { groupId, affectedOnly }: { groupId?: string | null; affectedOnly?: boolean }
): AssetCard[] =>
  cards.filter(
    (card) =>
      (!groupId || card.groupId === groupId) && (!affectedOnly || Boolean(card.signal))
  );

// Card order in localStorage like the masthead, but keyed per group, since one flat list would have every group fighting over it

const ASSET_ORDER_KEY = "erode.operations.assetOrder";

// Groups whose order is remembered; well past what one person arranges, and it stops renamed or deleted groups piling up
const ASSET_ORDER_GROUP_CAP = 50;

type AssetOrderMap = Record<string, string[]>;

const readAssetOrderMap = (): AssetOrderMap => {
  try {
    const raw = window.localStorage.getItem(ASSET_ORDER_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return parsed as AssetOrderMap;
  } catch {
    // A private window or a corrupt value must not take the widget down with it
    return {};
  }
};

/** The saved order for one group, or an empty list when it has never been arranged. */
export const readAssetOrder = (groupId: string): string[] => {
  const ids = readAssetOrderMap()[groupId];
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
};

export const writeAssetOrder = (groupId: string, ids: string[]): void => {
  try {
    // Re-inserted last so key order tracks recency, then trimmed to the newest groups so deleted ones age out
    const map = readAssetOrderMap();
    delete map[groupId];
    map[groupId] = ids;
    const entries = Object.entries(map).slice(-ASSET_ORDER_GROUP_CAP);
    window.localStorage.setItem(ASSET_ORDER_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Storage denied — the order still holds for this session, it just will not be remembered
  }
};

/** Merges a filtered view's reorder into the full order, so hidden assets keep their positions. */
export const applyVisibleOrder = (fullIds: string[], nextVisibleIds: string[]): string[] => {
  const moving = new Set(nextVisibleIds);
  let cursor = 0;
  return fullIds.map((id) => (moving.has(id) ? nextVisibleIds[cursor++] : id));
};

/** Apply a saved order to the cards on screen: dead ids drop out, and an asset added since lands at the end, never hidden. */
export const orderAssetCards = (cards: AssetCard[], order: string[]): AssetCard[] => {
  if (order.length === 0) return cards;
  const byId = new Map(cards.map((card) => [card.id, card]));
  const ordered = order.map((id) => byId.get(id)).filter((card): card is AssetCard => Boolean(card));
  const placed = new Set(ordered.map((card) => card.id));
  return [...ordered, ...cards.filter((card) => !placed.has(card.id))];
};
